import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HttpStatusCode, isAxiosError } from 'axios';
import { DeepPartial } from 'shared/types';
import { useCacheState } from 'state/useCacheState';
import {
  fetchGeoEvent,
  patchGeoEvent,
  postGeoEvent,
} from './useGeoEvent.queries';
import { constructPatch, invalidateGeoEventQueries } from './useGeoEvent.utils';
import {
  BaseCreateUpdateData,
  UseGeoEventEditableProps,
  UseGeoEventEditableResult,
  UseGeoEvent,
  UseGeoEventReadOnlyProps,
  UseGeoEventReadOnlyResult,
  SaveGeoEvent,
} from './useGeoEvent.types';

export const useGeoEvent = (<
  TGeoEvent extends BaseCreateUpdateData,
  TCreateUpdateData extends BaseCreateUpdateData = BaseCreateUpdateData,
  TPatchData extends
    DeepPartial<TCreateUpdateData> = DeepPartial<TCreateUpdateData>,
>(
  props: UseGeoEventReadOnlyProps | UseGeoEventEditableProps,
): UseGeoEventReadOnlyResult<TGeoEvent> &
  Partial<UseGeoEventEditableResult<TGeoEvent, TCreateUpdateData>> => {
  const { id: geoEventId, enabled = true, useCacheBuster = false } = props;

  const isEditable = 'dataPatchConfigs' in props;
  const dataPatchConfigs = isEditable
    ? (props as UseGeoEventEditableProps).dataPatchConfigs
    : {};
  const fieldPatchConfigs = isEditable
    ? ((props as UseGeoEventEditableProps).fieldPatchConfigs ?? {})
    : {};

  const queryClient = useQueryClient();
  const { cacheBusterTs } = useCacheState();

  const createGeoEventMutation = useMutation({
    mutationFn: postGeoEvent<TCreateUpdateData>,
  });

  const patchGeoEventMutation = useMutation({
    mutationFn: patchGeoEvent<TCreateUpdateData>,
  });

  const {
    data: geoEvent,
    error: geoEventFetchError,
    isError: isGeoEventFetchError,
    isLoading: isGeoEventLoading,
  } = useQuery({
    queryKey: ['geoEvent', geoEventId, useCacheBuster ? cacheBusterTs : null],
    queryFn: () =>
      fetchGeoEvent<TGeoEvent>(
        geoEventId!,
        useCacheBuster ? cacheBusterTs : null,
      ),
    enabled: enabled && Boolean(geoEventId),
    // if using cache buster, allow fetching the same geo event without re-fetching for a short time
    staleTime: useCacheBuster ? 1000 : undefined,
  });

  if (!isEditable) {
    return {
      geoEvent,
      geoEventFetchError,
      isGeoEventFetchError,
      isGeoEventLoading,
    };
  }

  const handleCreateGeoEvent = async (
    data: TCreateUpdateData,
    skipRedundancyCheck?: boolean,
  ): Promise<TCreateUpdateData> => {
    const createdGeoEvent = await createGeoEventMutation.mutateAsync({
      geoEventData: data,
      skipRedundancyCheck,
    });

    await invalidateGeoEventQueries(queryClient);
    return createdGeoEvent;
  };

  const handleUpdateGeoEvent = async (
    originalGeoEventId: number,
    patchData: TPatchData,
  ): Promise<TCreateUpdateData> => {
    const mutatedGeoEvent = await patchGeoEventMutation.mutateAsync({
      id: originalGeoEventId,
      geoEventData: patchData,
    });

    await invalidateGeoEventQueries(queryClient, originalGeoEventId);
    return mutatedGeoEvent;
  };

  const saveGeoEvent: SaveGeoEvent<TGeoEvent, TCreateUpdateData> = async (
    args,
  ) => {
    const { updatedGeoEvent, originalGeoEvent, shouldRetryOnDuplicate } = args;

    if (originalGeoEvent?.id) {
      const patchData = constructPatch<
        TGeoEvent,
        TCreateUpdateData,
        TPatchData
      >(originalGeoEvent, updatedGeoEvent, dataPatchConfigs, fieldPatchConfigs);

      if (patchData) {
        return handleUpdateGeoEvent(originalGeoEvent.id, patchData);
      }

      return null;
    }

    let mutatedGeoEvent = null;
    try {
      mutatedGeoEvent = await handleCreateGeoEvent(updatedGeoEvent);
    } catch (error: unknown) {
      if (!isAxiosError(error)) {
        throw error;
      }

      const errorStatus = error.response?.status;
      if (errorStatus === HttpStatusCode.Conflict) {
        const resp = await shouldRetryOnDuplicate?.(error);
        if (resp) {
          mutatedGeoEvent = await handleCreateGeoEvent(updatedGeoEvent, true);
          return mutatedGeoEvent;
        }

        return null;
      }

      throw error;
    }

    return mutatedGeoEvent;
  };

  return {
    geoEvent,
    geoEventFetchError,
    isGeoEventFetchError,
    isGeoEventLoading,
    saveGeoEvent,
  };
}) as UseGeoEvent;
